import React, { ChangeEvent, useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  PrimaryColor,
  TableBodyGrey,
  TableHeaderGrey,
  TableRowHover,
} from "../../shared/styles/colors";
import Log from "../../model/log";
import DatabaseEntities from "../../model/enum/DatabaseEntities";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DateConverter from "../../utils/DateConverter";
import { Card, CardContent, Typography, Button } from "@material-ui/core";
import { TablePagination } from "@mui/material";
import { Spinner } from "../../components";
import { ToastContainer } from "react-toastify";
import CachedIcon from "@mui/icons-material/Cached";
import ApiService from "../../service/ApiService";
import useStyles from "../styles/cardStyles";
import Misc from "../../utils/Misc";
import PageResultResponse from "../../model/pageResultResponse";

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: TableHeaderGrey,
    color: PrimaryColor,
    fontWeight: 700,
    whiteSpace: "nowrap",
    zIndex: 0,
    position: "static",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: TableBodyGrey,
    fontFamily: "monospace",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: TableRowHover,
  },
}));

const Logs = () => {
  const classes = useStyles();
  const [logs, setLogs] = useState<Log[]>([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const url = new URL(window.location.href);
      url.searchParams.set("page", page.toString());
      url.searchParams.set("rowsPerPage", rowsPerPage.toString());
      await ApiService.get<PageResultResponse>(
        `${DatabaseEntities.LOGS}?${url.searchParams.toString()}`,
      ).then((data: PageResultResponse) => {
        if (Misc.isArrayEmpty(data.items)) return;
        const { items, count } = data;
        setLogs(items);
        setTotalPages(count);
      });
      setLoading(false);
    };

    if (loading && Misc.isArrayEmpty(logs)) fetchData();
  });

  const handlePageChange = (
    event: React.ChangeEvent<unknown> | null,
    pg: number,
  ) => {
    setPage(pg + 1);
    scrollToTop();
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    let row = parseInt(event.target.value);
    setRowsPerPage(row);
    scrollToTop();
  };

  const scrollToTop = () => {
    setLogs([]);
    setLoading(true);
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  const resetList = (): void => {
    setLogs([]);
    setPage(1);
    setRowsPerPage(10);
    setLoading(true);
  };

  return (
    <>
      <Card raised className={classes.card}>
        <div className={classes.headerContainer}>
          <div className={classes.headerContainer}>
            <Typography className={classes.title}>Logs</Typography>
            <Button onClick={resetList}>
              <CachedIcon />
            </Button>
          </div>
        </div>
        <CardContent>
          <Paper>
            <TableContainer>
              <Table
                stickyHeader
                aria-label="customized table"
                padding="normal"
                size="medium"
              >
                <TableHead>
                  <TableRow>
                    <StyledTableCell align="right">Username</StyledTableCell>
                    <StyledTableCell align="right">Entity</StyledTableCell>
                    <StyledTableCell align="right">Entity Id</StyledTableCell>
                    <StyledTableCell align="right">Operation</StyledTableCell>
                    <StyledTableCell align="right">Status Code</StyledTableCell>
                    <StyledTableCell align="right">Errors</StyledTableCell>
                    <StyledTableCell align="right">Created At</StyledTableCell>
                    <StyledTableCell align="center">
                      <MoreVertIcon />
                    </StyledTableCell>
                  </TableRow>
                </TableHead>
                {loading ? (
                  <Spinner />
                ) : (
                  <TableBody>
                    {logs.length > 0 ? (
                      logs.map((row: Log) => (
                        <StyledTableRow key={row.id}>
                          <StyledTableCell align="right">
                            {row.User?.username}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {row.entityType}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {row.entityId}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {row.operation}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {row.statusCode}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {row.errorMessage}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {DateConverter.toFullDateAndHours(row.createdAt)}
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            <MoreVertIcon sx={{ color: PrimaryColor }} />
                          </StyledTableCell>
                        </StyledTableRow>
                      ))
                    ) : (
                      <StyledTableCell align="right">No items</StyledTableCell>
                    )}
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Paper>
          <TablePagination
            component="div"
            count={totalPages}
            page={page - 1}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>
      <ToastContainer />
    </>
  );
};

export default Logs;
